// components/EliteServices.jsx
import { useEffect, useRef, useState } from 'react';
import { FaGem, FaCrown, FaLaptop } from "react-icons/fa";
import '../../assets/styles/Services.css';
const { serviceData } = require('../../data/SeviceData.js');

// Map icon name strings to actual icon components
const iconMap = {
  FaLaptop: FaLaptop,
  FaGem: FaGem,
  FaCrown: FaCrown,
};

function ServiceCard({ service, idx, isExpanded, onExpand, cardsVisible, positionClass }){
  const Icon = iconMap[service.icon];
  const classNames = ['service-card'];
  if (cardsVisible) classNames.push('visible');
  if (isExpanded) classNames.push('expanded');
  if (positionClass) classNames.push(positionClass);

  return (
    <div
      className={classNames.join(' ')}
      aria-expanded={isExpanded}
      role="button"
      tabIndex={0}
      onClick={() => onExpand(idx)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onExpand(idx); }}
      style={{ animationDelay: `${idx * 180}ms` }}
    >
      <div className="icon-wrap">
        <div className="icon w-10 h-10 rounded-full border-2 border-blue-500 bg-slate-50 flex items-center justify-center">
          <Icon className="text-blue-500 w-6 h-6" />
        </div>
      </div>
      <h2 className="card-title text-gray-800">{service.title}</h2>
      <p className="card-tagLine">{service.tagLine}</p>

      <div className="card-actions">
        <button className="learn-more" onClick={(e) => { e.stopPropagation(); onExpand(idx); }}>
          Learn more
        </button>
      </div>
    </div>
  )
}

export default function EliteServices() {
  const gridRef = useRef();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [cardsVisible, setCardsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Mark grid visible and let React handle per-card visibility so it persists across re-renders
          gridRef.current.classList.add('visible');
          setCardsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className='service-bg'>
      <div className="align-middle services-container">
        <h2 id="services-heading" className="text-center text-4xl md:text-5xl font-bold mb-4 text-sky-950">
          Elite Services
        </h2>
        <p className="text-center text-sky-900 mb-12">
          Empowering ideas into powerful digital solutions through cutting-edge design, development, <br></br>and innovation to create digital excellence.
        </p>
        <div className="services-grid" ref={gridRef}>
          {serviceData.map((service, idx) => {
            const pos = idx % 3 === 0 ? 'left-card' : idx % 3 === 1 ? 'center-card' : 'right-card';
            return (
              <ServiceCard
                key={idx}
                service={service}
                idx={idx}
                isExpanded={expandedIndex === idx}
                onExpand={(i) => setExpandedIndex(i)}
                cardsVisible={cardsVisible}
                positionClass={pos}
              />
            );
          })}
        </div>

        {/* Expanded overlay */}
        {expandedIndex !== null && (
          <div className="expanded-overlay" role="dialog" aria-modal="true">
            <div className="expanded-card">
              <button className="close-expanded" onClick={() => setExpandedIndex(null)} aria-label="Close">×</button>
              <div className="expanded-content">
                <div className="expanded-header">
                  <h3>{serviceData[expandedIndex].title}</h3>
                  <p className="expanded-subtitle">{serviceData[expandedIndex].subTitle}</p>
                </div>
                <p className="expanded-description">{serviceData[expandedIndex].description}</p>
                <ul className="expanded-points">
                  {serviceData[expandedIndex].points.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
                <div className="expanded-actions">
                  <button className="btn-secondary" onClick={() => setExpandedIndex(null)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
